import { createLogic } from 'redux-logic';
import {
  createServiceRequestAction,
  IEvaServiceRequestAction,
} from '../action';
import { getServicePromise } from '../promise';
import { IEvaReduxStoreShoppingCartShape } from '../state/shopping-cart';
import { IEvaReduxStoreDefaultShape } from '../store';
import { createGetOrderWithStatusLogic, GetOrderWithStatus } from '../helpers';

/**
 * Creates all the EVA current order logic
 *
 * @export
 * @template SVC
 * @param {new () => SVC} serviceDefinition The EVA Service registry definition
 * @param {string} [stateKey] Optional state key name if not the same as the service name
 * @returns {Logic<IEvaServiceState<SVC>, any, any, any, any, string>}
 */
export function createCurrentOrderLogic<SHAPE extends IEvaReduxStoreDefaultShape>(
) {
  // Create the service request logics
  // These will handle the normal side effects of actually calling the EVA services
  // and its response and error handling
  //
  const currentOrderLogics = createGetOrderWithStatusLogic({ stateKey: 'currentOrder' });

  // This piece of logic will listen for the explicit current order update action and will update the
  // order if there is a current order id
  //
  const currentOrderUpdateLogic = createLogic<SHAPE, IEvaReduxStoreShoppingCartShape, any, any, any, string>({
    type: 'currentOrder:UPDATE',
    processOptions: {
      dispatchReturn: true,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const updateOrderAction = action as IEvaServiceRequestAction<GetOrderWithStatus, 'currentOrder:UPDATE'>;
      const payload = updateOrderAction.payload;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Collect the needed information from the current shopping cart
      // If we have no current order this action will be result in no action being taken.
      //
      const currentOrder = storeState.currentOrder;
      const currentOrderId = currentOrder?.data.response?.order.Result.ID;
      const requestedOrderId = payload.data?.order.OrderID || currentOrderId;

      if (requestedOrderId) {
        servicePromise.resolve();

        // Prepare the order with status action update request inserting
        // the current order id as the requested order
        //
        const currentOrderAction = createServiceRequestAction({
          service: GetOrderWithStatus,
          stateOptions: {
            stateKey: 'currentOrder',
          },
          payload: { order: { OrderID: requestedOrderId } },
          chainContext: payload.chainContext,
        });

        // Return the action that will trigger the order with status update
        //
        return currentOrderAction;
      }

      const noOrderFailure = new Error('No current order to update');
      servicePromise.reject(noOrderFailure);
      throw noOrderFailure;
    },
  });

  return [
    ...currentOrderLogics,
    currentOrderUpdateLogic,
  ];
}
